import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, KeyRound, Wrench, Users as UsersIcon, Calendar, AlertTriangle,
  TrendingUp, ArrowRight, Building2, RefreshCw,
} from "lucide-react";
import Layout from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { API } from "../../App";
import {
  UNIT_STATUS_LABELS, TENANT_STAGE_LABELS, formatINR,
} from "./_rentalHelpers";

const KPI = ({ icon: Icon, label, value, hint, accent = "slate", testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value ?? "—"}</p>
          {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${accent}-50`}>
          <Icon className={`w-5 h-5 text-${accent}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function RentalDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rentals/dashboard`);
      setData(res.data);
    } catch (e) {
      console.error("Rental dashboard load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tiles = [
    { path: "/rentals/units", label: "Units", icon: Home, hint: "Owner pipeline & listings" },
    { path: "/rentals/tenant-leads", label: "Tenant Leads", icon: UsersIcon, hint: "Inbound demand" },
    { path: "/rentals/visits", label: "Visits", icon: Calendar, hint: "Scheduled & completed" },
    { path: "/rentals/leases", label: "Leases", icon: KeyRound, hint: "Active & renewals" },
    { path: "/rentals/service-requests", label: "Service Requests", icon: Wrench, hint: "Tenant + owner ops" },
  ];

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="rental-dashboard">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-emerald-600" /> Rental Desk
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Owner onboarding → listing → tenant leads → visits → leases → service
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-rental-dashboard">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          <KPI testid="kpi-total-units" icon={Home} label="Total Units" value={data?.units?.total} accent="slate" />
          <KPI testid="kpi-listed" icon={TrendingUp} label="Listed" value={data?.units?.listed} accent="emerald" />
          <KPI testid="kpi-rented" icon={KeyRound} label="Rented Active" value={data?.units?.rented_active} accent="blue" />
          <KPI testid="kpi-vacant" icon={AlertTriangle} label="Vacant / Relist" value={data?.units?.vacant_relist} accent="rose" />
          <KPI testid="kpi-active-tenants" icon={UsersIcon} label="Active Tenant Leads" value={data?.tenant_leads?.active} accent="indigo" />
          <KPI testid="kpi-visits-week" icon={Calendar} label="Visits This Week" value={data?.visits?.this_week} hint={`${data?.visits?.upcoming || 0} upcoming`} accent="violet" />
          <KPI testid="kpi-active-leases" icon={KeyRound} label="Active Leases" value={data?.leases?.active} accent="emerald" />
          <KPI testid="kpi-open-service" icon={Wrench} label="Open Service" value={data?.service_requests?.open} accent="amber" />
        </div>

        {/* Renewals + Rent Roll */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card data-testid="renewals-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Renewals Due
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Next 30d", key: "renewals_30", color: "rose" },
                  { label: "Next 60d", key: "renewals_60", color: "amber" },
                  { label: "Next 90d", key: "renewals_90", color: "slate" },
                ].map(b => (
                  <div key={b.key} className={`p-3 rounded-lg bg-${b.color}-50 border border-${b.color}-100`}>
                    <p className={`text-xs text-${b.color}-700 font-semibold`}>{b.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{data?.leases?.[b.key] ?? 0}</p>
                  </div>
                ))}
              </div>
              <Button
                variant="link"
                className="mt-3 px-0 text-emerald-700"
                onClick={() => navigate("/rentals/leases?renewals=90")}
                data-testid="view-renewals"
              >
                View renewals due in 90d <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </CardContent>
          </Card>

          <Card data-testid="rent-roll-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" /> Rent Roll (read-only)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <p className="text-xs text-emerald-700 font-semibold">Active Monthly</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">
                    {formatINR(data?.rent_roll?.active_monthly)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-xs text-blue-700 font-semibold">Pipeline Expected</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">
                    {formatINR(data?.rent_roll?.pipeline_expected)}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                Summary only. No accounting, payments or payouts in Phase 1.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Section navigation */}
        <h2 className="text-base font-semibold text-slate-800 mb-2">Manage</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {tiles.map(t => (
            <button
              key={t.path}
              data-testid={`nav-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => navigate(t.path)}
              className="text-left p-4 bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md rounded-xl transition group"
            >
              <t.icon className="w-5 h-5 text-emerald-600 mb-2" />
              <p className="font-semibold text-slate-900">{t.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t.hint}</p>
              <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                Open <ArrowRight className="w-3 h-3" />
              </p>
            </button>
          ))}
        </div>

        {/* Pipeline breakdowns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card data-testid="unit-pipeline-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Unit Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {Object.entries(UNIT_STATUS_LABELS).map(([k, label]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="text-slate-700">{label}</span>
                    <Badge variant="outline" className="font-mono">{data?.units?.by_status?.[k] ?? 0}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="tenant-pipeline-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tenant Lead Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {Object.entries(TENANT_STAGE_LABELS).map(([k, label]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="text-slate-700">{label}</span>
                    <Badge variant="outline" className="font-mono">{data?.tenant_leads?.by_stage?.[k] ?? 0}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
